import React, { createContext, useContext, useMemo, useLayoutEffect } from 'react';
import { Spin } from 'antd';
import { moduleRegistry } from '@/modules/moduleRegistry';
import { ModuleDefinition, ClientModuleConfig } from '@/modules/types';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';
import { useEnabledModulesQuery } from '@/api/modules/queries';
import { useAdminClients } from '@/contexts/AdminClientsContext';

interface ModuleContextValue {
  /** All registered modules */
  allModules: ModuleDefinition[];
  /** Modules enabled for current client */
  enabledModules: ModuleDefinition[];
  /** Check if a module is enabled */
  isModuleEnabled: (moduleId: string) => boolean;
  /** Get module definition */
  getModule: (moduleId: string) => ModuleDefinition | undefined;
  /** Get client config for a module */
  getModuleConfig: (moduleId: string) => ClientModuleConfig | undefined;
  /** Loading state */
  loading: boolean;
}

const ModuleContext = createContext<ModuleContextValue | undefined>(undefined);

interface ModuleProviderProps {
  children: React.ReactNode;
}

/**
 * Module context provider
 * Manages module state and provides it to the application
 * 
 * NOTE: This provider should be placed above RouterProvider in the component tree
 * to ensure modules are loaded before routes render. It will read admin client ID
 * from URL search params when AdminClientSelectionProvider is not available.
 */
export const ModuleProvider: React.FC<ModuleProviderProps> = ({ children }) => {
  const { payload } = useAuth();
  const { tenantContext } = useTenant();
  
  // Get admin client selection from AdminClientsContext
  // AdminClientsProvider is above ModuleProvider, so it's available
  let adminSelectedClientId: string | null = null;
  try {
    const adminClients = useAdminClients();
    adminSelectedClientId = adminClients.selectedClientId;
  } catch {
    // AdminClientsProvider not available (shouldn't happen in normal flow)
    // Fallback to reading from URL for initial load
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      adminSelectedClientId = urlParams.get('adminClientId');
    }
  }
  
  // Determine the client ID to use:
  // 1. If admin has selected a client, use that
  // 2. Otherwise, use the user's clientId from payload
  // 3. Fall back to tenant context clientId
  const clientId = adminSelectedClientId || payload?.clientId || tenantContext.clientId;
  
  // Fetch enabled modules from API
  // For admin users without a selected client, the query will be disabled
  // Once the selector auto-selects and updates the URL, the query will automatically run
  const { data: enabledModulesData, isLoading } = useEnabledModulesQuery(clientId);

  // Sync registry with client configs in useLayoutEffect so the registry is populated
  // before children's useEffects run (e.g. React Query fetches). useLayoutEffect runs
  // synchronously after DOM mutations but before paint, so it runs before async effects.
  useLayoutEffect(() => {
    if (!clientId) return;
    const allInstances = moduleRegistry.getAllInstances();
    allInstances.forEach(instance => {
      if (instance.clientConfig?.clientId !== clientId) {
        instance.clientConfig = undefined;
        moduleRegistry.updateStatus(instance.definition.id, 'disabled');
      }
    });
    if (enabledModulesData?.modules) {
      enabledModulesData.modules.forEach((config: ClientModuleConfig) => {
        if (config.clientId !== clientId) return;
        const instance = moduleRegistry.getInstance(config.moduleId);
        if (instance) {
          instance.clientConfig = config;
          moduleRegistry.updateStatus(config.moduleId, 'enabled');
        }
      });
    }
  }, [clientId, enabledModulesData]);

  const contextValue = useMemo<ModuleContextValue>(() => {
    const allModules = moduleRegistry.getAllDefinitions();
    
    // Compute enabled modules directly from enabledModulesData
    // This ensures we get the correct list even if registry hasn't been updated yet
    const enabledModules = clientId && enabledModulesData?.modules
      ? enabledModulesData.modules
          .filter(config => config.clientId === clientId)
          .map(config => moduleRegistry.getDefinition(config.moduleId))
          .filter((def): def is ModuleDefinition => !!def)
      : [];

    return {
      allModules,
      enabledModules,
      isModuleEnabled: (moduleId: string) => {
        if (!clientId || !enabledModulesData?.modules) return false;
        return enabledModulesData.modules.some(
          config => config.moduleId === moduleId && config.clientId === clientId
        );
      },
      getModule: (moduleId: string) => {
        return moduleRegistry.getDefinition(moduleId);
      },
      getModuleConfig: (moduleId: string) => {
        if (!clientId || !enabledModulesData?.modules) return undefined;
        return enabledModulesData.modules.find(
          config => config.moduleId === moduleId && config.clientId === clientId
        );
      },
      loading: isLoading
    };
  }, [clientId, enabledModulesData, isLoading]);

  // Don't render children until modules are loaded (if we have a clientId)
  // For admins without client selection, we still render (they can select a client)
  const shouldWaitForModules = clientId && isLoading;

  if (shouldWaitForModules) {
    return (
      <div
        style={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px'
        }}>
        <Spin size="large" />
        <div style={{ color: '#666', fontSize: '14px' }}>
          Loading modules...
        </div>
      </div>
    );
  }

  return (
    <ModuleContext.Provider value={contextValue}>
      {children}
    </ModuleContext.Provider>
  );
};

/**
 * Hook to access module context
 */
export function useModules() {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModules must be used within ModuleProvider');
  }
  return context;
}

/**
 * Generic hook to get extension for a module by extension point key.
 * Use this for all domains (appointments, tasks, threads, etc.) — no domain-specific hooks.
 */
export function useModuleExtension<T>(moduleId: string | undefined, extensionPoint: string): T | undefined {
  const { isModuleEnabled } = useModules();
  return useMemo(() => {
    if (!moduleId || !isModuleEnabled(moduleId)) return undefined;
    return moduleRegistry.getExtension<T>(moduleId, extensionPoint);
  }, [moduleId, extensionPoint, isModuleEnabled]);
}

/**
 * Hook to check if a specific module is enabled
 */
export function useModule(moduleId: string) {
  const { isModuleEnabled, getModule, getModuleConfig } = useModules();
  const enabled = isModuleEnabled(moduleId);
  const definition = getModule(moduleId);
  const config = getModuleConfig(moduleId);

  return {
    enabled,
    definition,
    config,
    exists: !!definition
  };
}
